import {
  notifySubmitClickedOccurred$,
  notifySubmissionFailedOccurred$,
  notifyModalClosedOccurred$,
  notifyAbandonedOccurred$,
} from '../redux/trackingObservables';
import {
  startNotifySession,
  recordEmailFieldInteraction,
  markNotifySubmitted,
  getElapsedSeconds,
  getEmailEntered,
  getNotifySessionSnapshot,
  resetNotifySession,
} from './getOnTheListTrackingState';

export { recordEmailFieldInteraction };

/**
 * Fires as soon as the "Get on the list" form is displayed — this is also where the session
 * clock starts (drives time_to_submit later). Unlike Contact Builder's entry modal, this form
 * opens directly from a single data-lead-generator-opener click, so there's no separate
 * "option selected" step to hang this on — bindEvents() is the right place.
 */
export function trackNotifyFormOpened() {
  startNotifySession();
}

/**
 * Fires on a successful submit, right before the lead is actually posted. The email field is
 * always required, so a successful submit already guarantees the user provided one — no extra
 * check needed to satisfy the ticket's "only if the user provided an email" requirement.
 */
export function trackNotifySubmitClicked() {
  const { started } = getNotifySessionSnapshot();

  if (!started) {
    // No active session — defensive only, shouldn't happen since callers only invoke this while
    // their own form is mounted (see components/getOnTheListForm.js bindEvents).
    return;
  }

  const timeToSubmit = getElapsedSeconds();

  markNotifySubmitted();

  notifySubmitClickedOccurred$.next({ timeToSubmit });
}

/**
 * Fires when a submit attempt fails. Only 'validation' is produced today — server-side failure
 * detection would require the shared brochure epics/API (also used by the unrelated Free
 * Brochure and SRP extended-card flows) to observe fetch success/failure, which is out of scope.
 * @param {string} errorType
 */
export function trackNotifySubmissionFailed(errorType) {
  const { started } = getNotifySessionSnapshot();

  if (!started) {
    return;
  }

  notifySubmissionFailedOccurred$.next({ errorType });
}

/**
 * Fires on every modal close (X button, outside click, or Escape) while the form is mounted.
 * Also fires the abandonment Subject whenever the close isn't preceded by a successful submit —
 * unlike Contact Builder's abandonment, this doesn't require the user to have started filling
 * the form out first: the ticket asks for it on every non-submit exit, distinguishing the two
 * cases via emailFieldInteracted instead.
 * @param {{ closeTarget: 'x_button'|'outside_click'|'escape', formElement: HTMLFormElement|null }} params
 */
export function trackNotifyModalClosed({ closeTarget, formElement }) {
  const { started, submitted, emailFieldInteracted } = getNotifySessionSnapshot();

  if (!started) {
    return;
  }

  const emailEntered = getEmailEntered(formElement);

  notifyModalClosedOccurred$.next({ closeTarget, emailEntered });

  if (!submitted) {
    notifyAbandonedOccurred$.next({ emailFieldInteracted });
  }

  resetNotifySession();
}
